/** Next stage (Dean, 13 Oct 2026 — "legal is dynamic, let the calendar drive it"): one chip that always says what the bankruptcy
 *  route needs next. Click it (legal mode) for the ordered steps — record service / review outcome / petition first, then the
 *  move itself; Pause · Not appropriate · Close sit under "Other". Allowed moves come from the server (services/legal_route.py). */
import { useEffect, useState } from "react";
import { Route, ChevronDown, Pause, Play, Ban, XCircle, ArrowRight, History, FileCheck2, ShieldCheck, ClipboardList, ScrollText, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { getCaseGates, getCaseRoute, moveCaseRoute } from "@/lib/api";
import GateChecklistDialog from "@/pages/legal/GateChecklistDialog";
import ServiceRecordDialog from "@/pages/legal/ServiceRecordDialog";
import ReviewOutcomeDialog from "@/pages/legal/ReviewOutcomeDialog";
import PetitionHubDialog from "@/pages/legal/petition/PetitionHubDialog";

const REVIEW_STATUSES = ["sd_served", "post_demand_review"];
const PETITION_STATUSES = ["prepare_petition", "awaiting_hearing", "order_made", "referred_menzies"];
const SERVICE_STATUSES = ["ready_for_sd", "sd_served"];

const TONE = {
  referred_unassigned: "bg-slate-100 text-slate-700 border-slate-300",
  eligibility_check: "bg-sky-50 text-sky-800 border-sky-300",
  pre_action: "bg-amber-50 text-amber-900 border-amber-300",
  ready_for_sd: "bg-orange-50 text-orange-900 border-orange-300",
  sd_served: "bg-orange-100 text-orange-900 border-orange-400",
  post_demand_review: "bg-indigo-50 text-indigo-800 border-indigo-300",
  prepare_petition: "bg-violet-50 text-violet-800 border-violet-300",
  awaiting_hearing: "bg-rose-50 text-rose-800 border-rose-300",
  order_made: "bg-slate-800 text-white border-slate-800",
  referred_menzies: "bg-emerald-50 text-emerald-800 border-emerald-300",
  route_paused: "bg-yellow-50 text-yellow-900 border-yellow-300",
  not_appropriate: "bg-slate-50 text-slate-600 border-slate-300",
  route_closed: "bg-slate-50 text-slate-500 border-slate-200",
};
const ICON = { route_paused: Pause, route_closed: XCircle, not_appropriate: Ban };

export default function RouteStatusControl({ caseRow, onChanged, readOnly = false, onRouteLoaded }) {
  const [route, setRoute] = useState(null);
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [gateFor, setGateFor] = useState(null);      // {gate, to, label}
  const [service, setService] = useState(null);     // service methods list when the dialog is open
  const [review, setReview] = useState(false);      // Stage 4 "Record review outcome" dialog
  const [petition, setPetition] = useState(false);  // C1 petition hub (Stages 5 → 6)

  const load = async () => {
    try { const r = await getCaseRoute(caseRow.case_id); setRoute(r); onRouteLoaded?.(r); } catch { setRoute(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caseRow.case_id, caseRow.bankruptcy_route?.status]);

  const applied = async (res, to) => {
    const n = (res.created_events || []).length;
    toast.success(`Bankruptcy route → ${res.route?.label || to}${n ? ` · ${n} action${n === 1 ? "" : "s"} diarised` : ""}${res.ladder_stage ? ` · stage ${res.ladder_stage}` : ""}`);
    setPending(null); setReason(""); setGateFor(null);
    setRoute(res.route);
    await onChanged?.();
  };
  const doMove = async (to, why) => {
    setBusy(true);
    try {
      await applied(await moveCaseRoute(caseRow.case_id, to, why), to);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.gate) {
        const m = (route?.allowed || []).find((x) => x.to === to);
        setGateFor({ gate: d.gate.gate, to, label: m?.label || to });
        toast.error(d.message);
      } else {
        toast.error("Route move failed: " + (d?.message || d || e.message));
      }
    } finally { setBusy(false); }
  };
  const pick = (m) => (["awaiting_hearing", "order_made", "referred_menzies"].includes(m.to) ? setPetition(true) : m.reason_required ? setPending(m) : m.gate ? setGateFor({ gate: m.gate, to: m.to, label: m.label }) : doMove(m.to));
  const openService = async () => {
    try { const g = await getCaseGates(caseRow.case_id); setService(g.service_methods || []); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  if (!route) return null;
  const allowed = route.allowed || [];
  const chip = "h-8 inline-flex items-center gap-1.5 px-2.5 rounded-md border text-xs font-medium";
  if (!route.on_route) {
    const start = allowed.find((m) => m.start);
    if (!start) return null;
    if (readOnly) return <span className={`${chip} ${TONE.referred_unassigned}`} data-testid="next-stage-chip"><Flag className="w-3.5 h-3.5" /> Next stage: {start.label}</span>;
    return (
      <Button size="sm" variant="outline" className="h-8 text-xs border-red-200 text-red-800 bg-red-50/60 hover:bg-red-100" onClick={() => doMove(start.to)} disabled={busy} data-testid="route-start-btn" title="Put this file on the E&W personal bankruptcy route — eligibility check first">
        <Flag className="w-3.5 h-3.5 mr-1" /> Next stage: {start.label}
      </Button>
    );
  }
  const Icon = ICON[route.status] || Route;
  const forward = allowed.filter((m) => !m.reason_required);
  const side = allowed.filter((m) => m.reason_required);
  const resume = forward.find((m) => m.resume);
  const doNow = [
    SERVICE_STATUSES.includes(route.status) && { key: "service", label: "Record Statutory Demand service…", icon: FileCheck2, tone: "text-orange-800", run: openService, testid: "route-record-service" },
    REVIEW_STATUSES.includes(route.status) && { key: "review", label: "Record review outcome…", icon: ClipboardList, tone: "text-indigo-800", run: () => setReview(true), testid: "route-record-review" },
    PETITION_STATUSES.includes(route.status) && { key: "petition", label: "Bankruptcy petition…", icon: ScrollText, tone: "text-violet-800", run: () => setPetition(true), testid: "route-petition-hub" },
  ].filter(Boolean);
  const text = route.terminal ? route.label
    : route.status === "route_paused" ? `Paused${resume ? ` · resume → ${resume.label}` : ""}`
    : forward[0] ? `${route.label} → ${forward[0].label}` : route.label;
  const title = `Bankruptcy route — now ${route.label}${route.stage_no ? ` (stage ${route.stage_no})` : ""}${route.since ? ` since ${new Date(route.since).toLocaleDateString("en-GB")}` : ""}${forward[0] && !route.terminal ? ` · next: ${forward[0].label}${forward[0].gate ? ` (gate ${forward[0].gate})` : ""}` : ""}`;
  if (readOnly) {
    return <span className={`${chip} ${TONE[route.status] || TONE.route_closed}`} title={title} data-testid="next-stage-chip"><Icon className="w-3.5 h-3.5" /> {route.terminal ? "" : "Next stage: "}{text}</span>;
  }
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={`${chip} ${TONE[route.status] || TONE.route_closed}`} disabled={busy} data-testid="route-chip" title={title}>
            <Icon className="w-3.5 h-3.5" /> {route.terminal ? "" : "Next stage: "}{text}
            {!route.terminal && <ChevronDown className="w-3 h-3 opacity-70" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="text-xs w-80" data-testid="route-menu">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-500">Now: {route.label}{route.stage_no != null ? ` · stage ${route.stage_no}` : ""}</DropdownMenuLabel>
          {route.reason && <div className="px-2 pb-1 text-[11px] text-slate-600 italic truncate" title={route.reason}>“{route.reason}”</div>}
          {doNow.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Do now</DropdownMenuLabel>
              {doNow.map((d) => { const I = d.icon; return (
                <DropdownMenuItem key={d.key} onClick={d.run} className={`${d.tone} font-medium`} data-testid={d.testid}><I className="w-3.5 h-3.5 mr-2" /> {d.label}</DropdownMenuItem>
              ); })}
            </>
          )}
          {forward.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Then move to</DropdownMenuLabel>
              {forward.map((m) => (
                <DropdownMenuItem key={m.to} onClick={() => pick(m)} data-testid={`route-move-${m.to}`}>
                  {m.resume ? <Play className="w-3.5 h-3.5 mr-2 text-emerald-600" /> : m.gate ? <ShieldCheck className="w-3.5 h-3.5 mr-2 text-indigo-600" /> : <ArrowRight className="w-3.5 h-3.5 mr-2 text-slate-500" />}
                  <span className="flex-1">{m.resume ? `Resume — ${m.label}` : m.label}</span>
                  {m.gate && <span className="ml-2 text-[9px] font-mono px-1 rounded border bg-indigo-50 text-indigo-700 border-indigo-200" title={`Gate ${m.gate} — the checklist opens before this move`}>{m.gate}</span>}
                </DropdownMenuItem>
              ))}
            </>
          )}
          {side.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Other</DropdownMenuLabel>
              {side.map((m) => {
                const I = ICON[m.to] || Ban;
                return (
                  <DropdownMenuItem key={m.to} onClick={() => pick(m)} className="text-slate-600" data-testid={`route-move-${m.to}`}>
                    <I className="w-3.5 h-3.5 mr-2" /> {m.label}<span className="ml-auto text-[9px] text-slate-400">reason</span>
                  </DropdownMenuItem>
                );
              })}
            </>
          )}
          {route.history?.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <div className="px-2 py-1 text-[10px] text-slate-500 flex items-center gap-1" data-testid="route-history"><History className="w-3 h-3" /> {route.history.length} move{route.history.length === 1 ? "" : "s"} · last by {route.history[route.history.length - 1].by} {new Date(route.history[route.history.length - 1].at).toLocaleDateString("en-GB")}</div>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!pending} onOpenChange={(o) => { if (!o) { setPending(null); setReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="route-reason-dialog">
          <DialogHeader><DialogTitle className="text-sm">{pending?.label} — why?</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Logged on the file's timeline and in the route history. 5+ characters.</p>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="text-sm" placeholder={pending?.to === "route_paused" ? "e.g. Breathing Space in force until 12/11" : pending?.to === "not_appropriate" ? "e.g. Debt below £5,000 threshold / guarantor already bankrupt" : "e.g. Paid in full / arrangement agreed and performing"} data-testid="route-reason-input" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setPending(null); setReason(""); }} data-testid="route-reason-cancel">Cancel</Button>
            <Button size="sm" className="bg-red-700 hover:bg-red-800" onClick={() => doMove(pending.to, reason)} disabled={busy || reason.trim().length < 5} data-testid="route-reason-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {gateFor && (
        <GateChecklistDialog caseRow={caseRow} gate={gateFor.gate} move={{ to: gateFor.to, label: gateFor.label }}
          onClose={() => setGateFor(null)} onMoved={(res) => applied(res, gateFor.to)} />
      )}
      {service && (
        <ServiceRecordDialog caseRow={caseRow} methods={service} onClose={() => setService(null)}
          onSaved={async (r) => { setService(null); if (r.route) setRoute(r.route); await onChanged?.(); }} />
      )}
      {review && (
        <ReviewOutcomeDialog caseRow={caseRow} onClose={() => setReview(false)}
          onDone={async (r) => { setReview(false); if (r?.route) setRoute(r.route); else await load(); await onChanged?.(); }} />
      )}
      {petition && (
        <PetitionHubDialog caseRow={caseRow} onClose={() => setPetition(false)}
          onRouteChanged={async (r) => { if (r) setRoute(r); else await load(); await onChanged?.(); }} />
      )}
    </>
  );
}
